import { useState, Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CaptureButton from "@/components/CaptureButton";
import { 
  Users,
  User,
  Kanban, 
  Mail, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  BookOpen,
  FileText,
  Bot,
  Radar,
  ShieldCheck,
  BarChart3,
  ListChecks,
  Inbox,
  Lightbulb,
  Cog,
  Globe,
  Paintbrush,
  Telescope,
  MailQuestion,
  Search,
  Target,
  Zap,
  ClipboardList,
  Send,
  MailPlus,
  TrendingUp,
  ScanLine,
  Radio,
  Briefcase,
  LayoutDashboard,
  Image as ImageIcon,
  Palette,
  Presentation,
  Sparkles,
  HeartHandshake,
  PackageCheck,
  CalendarClock,
  Gavel,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { API } from "@/lib/constants";
import AskRexWidget from "@/components/AskRexWidget";
import BudgetHeadroomIndicator from "@/components/BudgetHeadroomIndicator";
import { useWorkspace, CURRENCIES, REGIONS } from "@/lib/workspaceContext";

// ─── Header pill: Currency / Region selector ─────────────
function HeaderPill({ label, value, options, onChange, testid }) {
  return (
    <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 bg-white text-xs">
      <span className="text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-0 outline-none font-semibold text-slate-800 cursor-pointer"
        data-testid={testid}
      >
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </div>
  );
}

// ─── Environment Banner ──────────────────────────────────
function EnvBanner() {
  // Host-based detection (May 2026) — env var is unreliable once deployed;
  // instead read window.location.host so the banner is driven by the
  // actual URL the user is on. askrex.info (and any subdomain) = live
  // production → NO banner. Everything else (preview, localhost) = show
  // the amber "preview data" banner.
  const host = (typeof window !== "undefined" && window.location && window.location.host) || "";
  const isProduction = /(^|\.)askrex\.info$/i.test(host.split(":")[0]);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem("rex-env-banner-dismissed") === "1"; }
    catch (_e) { return false; }
  });
  if (hidden) return null;
  if (isProduction) return null;
  const dismiss = () => {
    try { sessionStorage.setItem("rex-env-banner-dismissed", "1"); } catch (_e) {}
    setHidden(true);
  };
  return (
    <div
      className="w-full bg-amber-100 border-b border-amber-300 text-amber-900 text-sm px-4 py-2 flex items-center justify-between"
      data-testid="env-banner"
    >
      <span>
        <strong>PREVIEW environment</strong> — test data only. For live data, log in at{" "}
        <a href="https://askrex.info" className="underline font-semibold">askrex.info</a>.
      </span>
      <button
        onClick={dismiss}
        className="text-amber-800 hover:text-amber-900 ml-4 text-xs font-semibold"
        data-testid="env-banner-dismiss"
      >
        Dismiss ✕
      </button>
    </div>
  );
}

// ─── REX v3 — Role-based 7-surface sidebar ─────────────────────
// Each surface declares which roles are allowed to see it. Admins/owners
// see everything. Designers and reviewers get a narrow, focused nav.
// Feb 2026 IA refactor — consolidating legacy dashboards behind the v3 tabs.
const ALL_ROLES = ["admin", "owner", "mp", "manager", "leader", "editor", "approver", "designer", "reviewer", "viewer", "user"];
const MANAGEMENT_ROLES = ["admin", "owner", "mp", "manager", "leader", "editor", "approver"];
const CREATIVE_ROLES = ["admin", "owner", "mp", "manager", "leader", "editor", "approver", "designer", "reviewer"];

const primaryNavItems = [
  { path: "/today", label: "Today", icon: Radio, roles: ALL_ROLES },
  { path: "/agenda", label: "This Week", icon: CalendarClock, roles: MANAGEMENT_ROLES },
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: MANAGEMENT_ROLES },
  { path: "/grow", label: "Grow", icon: TrendingUp, roles: MANAGEMENT_ROLES },
  { path: "/research", label: "Research", icon: Search, roles: MANAGEMENT_ROLES },
  { path: "/pattern-review", label: "Pattern Review", icon: BarChart3, roles: MANAGEMENT_ROLES },
  { path: "/clients", label: "Clients", icon: Users, roles: MANAGEMENT_ROLES },
  { path: "/recommendations", label: "Recommendations", icon: Lightbulb, roles: MANAGEMENT_ROLES },
  { path: "/nurture", label: "Nurture", icon: HeartHandshake, roles: MANAGEMENT_ROLES },
  { path: "/engagements", label: "Engagements", icon: Briefcase, roles: MANAGEMENT_ROLES },
  { path: "/delivery", label: "Delivery", icon: PackageCheck, roles: MANAGEMENT_ROLES },
  { path: "/amplify", label: "Amplify", icon: Paintbrush, roles: CREATIVE_ROLES },
  { path: "/content-studio", label: "Content Studio", icon: Sparkles, roles: CREATIVE_ROLES },
  { path: "/campaigns", label: "Campaigns", icon: Send, roles: MANAGEMENT_ROLES },
  { path: "/lens", label: "Lens", icon: Telescope, roles: ALL_ROLES },
  { path: "/awaiting-replies", label: "Awaiting Replies", icon: MailQuestion, roles: MANAGEMENT_ROLES },
  { path: "/triage", label: "Triage", icon: Gavel, roles: MANAGEMENT_ROLES },
  { path: "/ask", label: "Ask Rex", icon: Bot, roles: ALL_ROLES },
  { path: "/plans", label: "Action Plans", icon: ClipboardList, roles: ALL_ROLES },
];

// ─── Proposal Studio (May 2026) ───────────────────────────
// Re-surfaced after a Feb 2026 IA refactor accidentally dropped these
// commercial/creation pages from the sidebar. Grouped as a collapsible
// section placed directly after Lens. Gated to creative/management roles.
const proposalStudioNavItems = [
  { path: "/proposals", label: "Proposals", icon: FileText },
  { path: "/presentations", label: "Presentations", icon: Presentation },
  { path: "/brand-manager", label: "Brand Manager", icon: Palette },
];

// Help section — always visible to every user. Surfaced as top-level
// couldn't find the Knowledge Base and User Guide while they were
// buried inside the collapsed Settings drawer.
const helpNavItems = [
  { path: "/knowledge-base", label: "Knowledge Base", icon: BookOpen },
  { path: "/guide", label: "User Guide", icon: BookOpen },
];

// Settings drawer (collapsed by default).
// Items flagged ``adminOnly`` are filtered out for non-admins so the section
// stays useful (Notifications) without exposing user-management, FX rates,
// the Admin Console, or the Rex inbox to ordinary accounts.
const settingsNavItems = [
  { path: "/notifications", label: "Notifications", icon: Inbox },
  { path: "/rex-inbox", label: "Rex Inbox", icon: Inbox, adminOnly: true },
  { path: "/integration-health", label: "Integration Health", icon: ShieldCheck, adminOnly: true },
  { path: "/users", label: "Users", icon: Users, adminOnly: true },
  { path: "/fx-rates", label: "FX Rates", icon: Globe, adminOnly: true },
  { path: "/settings", label: "Admin Console", icon: Settings, adminOnly: true },
];

// Legacy items still reachable (not in nav, redirected from old surfaces)
const intelligenceNavItems = [];

// ─── TIER 3: Commercial (Weekly) ──────────────────────
// Legacy commercial items (kept for routes that still resolve directly)
const commercialNavItems = [];

// ─── Admin Items ──────────────────────────────────────────────
// Deliberately empty (May 2026) — the dedicated Admin section was
// removed because every admin-only entry already lives in the Settings
// drawer above with the ``adminOnly`` flag. Keeping the array exported
// avoids touching every call-site that referenced it historically.
const adminNavItems = [];

// Returns the subset of primaryNavItems visible to the given user role.
// Feb 2026 — gating temporarily removed at user request (only "admin" gets
// the dedicated Admin section + Settings drawer below). Every signed-in user
// now sees the full primary surface set so accounts created with default
// role="user" don't end up with just Today + Ask Rex in their sidebar.
function visibleNav(user) {
  // Filter out adminOnly items for non-admin users. The role check
  // matches the same lower-case comparison used in the Settings drawer
  // render path so behaviour stays consistent.
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  return primaryNavItems.filter((item) => !item.adminOnly || isAdmin);
}

export default function Layout({ children, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commercialOpen, setCommercialOpen] = useState(false);
  const [intelligenceOpen, setIntelligenceOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [proposalStudioOpen, setProposalStudioOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-expand sections if current path is in them
  const isInCommercial = commercialNavItems.some(item => location.pathname.startsWith(item.path));
  const isInIntelligence = intelligenceNavItems.some(item => location.pathname.startsWith(item.path));
  const isInSettings = settingsNavItems.some(item => location.pathname.startsWith(item.path));
  const isInProposalStudio = proposalStudioNavItems.some(item => location.pathname.startsWith(item.path));

  // Proposal Studio is gated to creative/management roles (not plain viewers).
  const canSeeProposalStudio = CREATIVE_ROLES.includes((user?.role || "").toLowerCase());

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const NavLink = ({ item, isActive }) => {
    const Icon = item.icon;
    return (
      <Link
        to={item.path}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-blue-50 text-blue-600"
            : "text-slate-600 hover:bg-slate-100"
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon className="w-5 h-5" />
        {item.label}
      </Link>
    );
  };

  const CollapsibleSection = ({ label, icon: Icon, items, isOpen, onToggle, autoOpen }) => {
    const open = isOpen || autoOpen;
    return (
      <div className="space-y-1">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Icon className="w-4 h-4" />
            {label}
          </span>
          {open ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        {open && (
          <div className="space-y-1 pl-2">
            {items.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
              return <NavLink key={item.path} item={item} isActive={isActive} />;
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      <EnvBanner />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_669db3fc-fcfc-4e61-838e-67066d41b913/artifacts/gfacdued_REX.png" 
                alt="REX" 
                className="h-10"
                data-testid="sidebar-rex-logo"
              />
            </Link>
            <button
              className="lg:hidden p-1 hover:bg-slate-100 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation — REX v3 (single tier + collapsed Settings) */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {visibleNav(user).map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
              return (
                <Fragment key={item.path}>
                  <NavLink item={item} isActive={isActive} />
                  {item.path === "/lens" && canSeeProposalStudio && (
                    <CollapsibleSection
                      label="Proposal Studio"
                      icon={Sparkles}
                      items={proposalStudioNavItems}
                      isOpen={proposalStudioOpen}
                      onToggle={() => setProposalStudioOpen(!proposalStudioOpen)}
                      autoOpen={isInProposalStudio}
                    />
                  )}
                </Fragment>
              );
            })}

            {/* Help — always visible (May 2026). Knowledge Base + User Guide
                are surfaced at top level so non-admins can find them without
                expanding the Settings drawer. */}
            <div className="pt-4 pb-1 px-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Help</p>
            </div>
            {helpNavItems.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
              return <NavLink key={item.path} item={item} isActive={isActive} />;
            })}

            <div className="pt-6" />

            {/* Settings drawer — un-gated Feb 2026 (was admin/manager-only).
                Items flagged ``adminOnly`` are still filtered out for
                non-admins. Admin-only entries (Rex Inbox, Users, FX
                Rates, Admin Console) now live INSIDE the Settings drawer
                — the separate Admin block was removed in May 2026 to
                stop showing each item twice. */}
            <CollapsibleSection
              label="Settings"
              icon={Cog}
              items={settingsNavItems.filter(
                (it) => !it.adminOnly || (user?.role || "").toLowerCase() === "admin"
              )}
              isOpen={settingsOpen}
              onToggle={() => setSettingsOpen(!settingsOpen)}
              autoOpen={isInSettings}
            />
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-100 transition-colors">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-blue-100 text-blue-600 text-sm font-medium">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {user?.name || "User"}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {user?.email || ""}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b border-slate-200 lg:px-6">
          <button
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          
          <div className="flex-1" />

          {/* AI budget headroom indicator — only visible to admins, only
              renders when MTD spend is at amber/red/blocked thresholds.
              Replaced the per-generation cost pills (Remi 2026-05-28). */}
          <div className="mr-2">
            <BudgetHeadroomIndicator user={user} />
          </div>

          {/* REX v3 — currency + region selectors. Hidden on pages that
              don't read them (Today / Planner / Ask Rex / Inbox / Settings)
              to reduce visual noise — they're only meaningful on Revenue,
              Engagements, Programmes, Grow. */}
          {!["/", "/today", "/morning-scrum", "/amplify", "/ask-rex", "/ask", "/inbox", "/settings"].some(
            (p) => location.pathname === p || location.pathname.startsWith(p + "/")
          ) && <CurrencyRegionPills />}

          {/* Ask Rex Widget — hide on full Ask Rex page */}
          {location.pathname !== "/ask-rex" && location.pathname !== "/ask" && <AskRexWidget />}
        </header>

        {/* Page content */}
        <main className="min-h-[calc(100vh-4rem)]">
          {children}
        </main>
        <CaptureButton />
      </div>
    </div>
  );
}


// Header bar pills (currency + region) — REX v3 §2.3
function CurrencyRegionPills() {
  const { currency, region, setCurrency, setRegion } = useWorkspace();
  return (
    <div className="flex items-center gap-2 mr-3" data-testid="header-pills">
      <HeaderPill
        label="Currency"
        value={currency}
        options={CURRENCIES}
        onChange={setCurrency}
        testid="header-currency-select"
      />
      <HeaderPill
        label="Region"
        value={region}
        options={REGIONS}
        onChange={setRegion}
        testid="header-region-select"
      />
    </div>
  );
}
